import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { VANS_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import VanFormDialog from "./VanFormDialog";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import ZoneAssignmentSearchInput from "./ZoneAssignmentSearchInput";
import { formatWithTzLabel } from "../../utils/dateUtils";

const columns = [
  { id: "name", label: "Van" },
  { id: "zone", label: "Zone", render: (v) => v.zone?.name },
  { id: "driver", label: "Driver", render: (v) => v.driver?.name },
  { id: "updated_by", label: "Updated By", render: (v) => v.updated_by?.name },
  {
    id: "updated_at",
    label: "Updated On",
    render: (v) =>
      v.updated_at ? formatWithTzLabel(v.updated_at, "DD/MM/YYYY") : "",
  },
];

export default function UsersTable({
  searchInput,
  updateSearchValue,
  updateRefreshKey,
}) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [vansData, setUsersData] = useState([]);
  const [vanModalState, setvanModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const searchURL = searchInput ? `&search_id=${searchInput}` : "";
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-tenant-ID": tenant,
        };
        const response = await fetch(
          VANS_ENDPOINT +
            `?limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}` +
            searchURL,
          { headers },
        );
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === "user_inactive") {
            auth.logOut();
            navigate("/login");
          }
          throw new Error("Failed to fetch data");
        }
        const jsonData = await response.json();
        setUsersData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [controller, refreshKey, searchInput]);

  const tableColumns = [
    ...columns,
    {
      id: "action",
      label: "Action",
      align: "center",
      render: (v) => (
        <ButtonOne
          variant="contained"
          onClick={() =>
            setvanModalState({ isOpen: true, selectedClient: v, mode: "edit" })
          }
        >
          Edit
        </ButtonOne>
      ),
    },
  ];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        <ZoneAssignmentSearchInput
          updateSearchValue={updateSearchValue}
          updateRefreshKey={setRefreshKey}
        />
        <ButtonOne
          variant="contained"
          onClick={() =>
            setvanModalState({
              isOpen: true,
              selectedClient: null,
              mode: "add",
            })
          }
          sx={{ margin: 2 }}
        >
          Add Van
        </ButtonOne>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={vansData}
        rowKey={(v, i) => v.name ?? i}
        emptyMessage="No match found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) =>
          setController((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setController((prev) => ({
            ...prev,
            rowsPerPage: parseInt(e.target.value, 10),
            page: 0,
          }))
        }
      />

      <VanFormDialog
        modalState={vanModalState}
        handleModalState={setvanModalState}
        selectedClient={vanModalState.selectedClient}
        refreshTable={setRefreshKey}
      />
    </Paper>
  );
}
